/**
 * IndianFamilyTreeStatePage — SEO-friendly landing for a single Indian
 * state (or any country/region). Powered by the /family-trees/discover
 * endpoint. Deep-linkable: /state/kerala, /state/tamil-nadu, etc.
 *
 * Route:  /state/:stateSlug
 * Example: /state/kerala  →  "Kerala Family Trees on IndianFamilyTree.com"
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, TreePine, MapPin, ArrowRight, Sparkles, ScrollText,
         Users, Compass, BookOpen } from "lucide-react";
import { heritageBySlug } from "@/data/indianStatesHeritage";

const API = process.env.REACT_APP_BACKEND_URL;

const P = {
  bg: "#FDF9EF", ink: "#3A1F0F", inkSoft: "#6B4A2E",
  saffron: "#B25D38", saffronSoft: "#F7D6C1",
  green: "#0E7C55", greenSoft: "#C6EAD6",
};

// Slug → canonical state display name. Extendable to any country.
const STATE_ALIASES = {
  "kerala": "Kerala", "tamil-nadu": "Tamil Nadu", "karnataka": "Karnataka",
  "andhra-pradesh": "Andhra Pradesh", "telangana": "Telangana",
  "maharashtra": "Maharashtra", "goa": "Goa", "gujarat": "Gujarat",
  "rajasthan": "Rajasthan", "punjab": "Punjab", "delhi": "Delhi",
  "uttar-pradesh": "Uttar Pradesh", "west-bengal": "West Bengal",
  "odisha": "Odisha", "bihar": "Bihar", "assam": "Assam",
  "meghalaya": "Meghalaya", "haryana": "Haryana",
  "himachal-pradesh": "Himachal Pradesh", "madhya-pradesh": "Madhya Pradesh",
  "chhattisgarh": "Chhattisgarh", "jharkhand": "Jharkhand",
  "manipur": "Manipur", "mizoram": "Mizoram", "nagaland": "Nagaland",
  "sikkim": "Sikkim", "tripura": "Tripura",
  "arunachal-pradesh": "Arunachal Pradesh", "uttarakhand": "Uttarakhand",
};

export default function IndianFamilyTreeStatePage() {
  const { stateSlug } = useParams();
  const heritage      = heritageBySlug(stateSlug);
  const stateName     = heritage?.name
                     || STATE_ALIASES[stateSlug?.toLowerCase()]
                     || stateSlug?.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
                     || "Unknown State";

  const [data, setData] = useState({ items: [], total_trees: 0, total_members: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = `${stateName} Family Trees — Indian Family Tree`;
    // Set meta description for SEO.
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    meta.content = `Discover family trees, memorials and heritage records from ${stateName}, India on IndianFamilyTree.com. Add yours or find a lineage you're researching.`;
  }, [stateName]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        // Simple GET — no custom headers → preflight-free cross-origin.
        const r = await axios.get(
          `${API}/api/family-trees/discover?country=India&state=${encodeURIComponent(stateName)}&limit=60`
        );
        if (!cancelled) setData(r.data);
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [stateName]);

  const trees = data.items || [];

  return (
    <div style={{ background: P.bg, color: P.ink, minHeight: "100vh" }} data-testid="ift-state-page">
      <section className="relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${P.saffronSoft} 0%, ${P.bg} 60%, ${P.greenSoft} 100%)` }}>
        <div className="max-w-5xl mx-auto px-4 py-12 lg:py-16">
          <Link to="/" className="text-xs inline-flex items-center gap-1 opacity-70 hover:opacity-100" style={{ color: P.saffron }}>
            ← All India
          </Link>
          <div className="mt-3 inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-mono tracking-wider" style={{ borderColor: P.saffron, color: P.saffron, background: "#fff" }}>
            <MapPin className="w-3.5 h-3.5" /> {stateName.toUpperCase()}
          </div>
          <h1 className="mt-3 font-display font-bold text-4xl lg:text-6xl leading-tight" style={{ color: P.ink }} data-testid="ift-state-title">
            {stateName} <span style={{ color: P.saffron }}>Family Trees</span>
          </h1>
          <p className="mt-3 text-base lg:text-lg max-w-2xl" style={{ color: P.inkSoft }}>
            {trees.length > 0
              ? <>Discover <b>{data.total_members?.toLocaleString?.() || 0}</b> ancestors documented across <b>{data.total_trees}</b> family {data.total_trees === 1 ? "tree" : "trees"} rooted in {stateName}.</>
              : <>Be the first to plant a family tree from {stateName} on this network.</>}
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/family-trees/new"
              className="inline-flex items-center gap-2 rounded-full px-6 py-3 font-semibold text-white shadow-md hover:shadow-lg transition"
              style={{ background: P.saffron }}
              data-testid="ift-state-start-tree"
            >
              <TreePine className="w-4 h-4" /> Start a {stateName} tree
            </Link>
          </div>
        </div>
      </section>

      {/* ── Heritage sections (data-driven from indianStatesHeritage.js) ── */}
      {heritage && (
        <section className="max-w-5xl mx-auto px-4 py-10 space-y-8"
                 data-testid="ift-state-heritage">
          {/* Overview card — history, formed year, languages */}
          <div className="rounded-2xl border p-6 bg-white space-y-4"
               style={{ borderColor: P.saffronSoft }}
               data-testid="ift-state-history">
            <div className="flex flex-wrap items-baseline gap-3">
              <h2 className="font-display text-2xl" style={{ color: P.ink }}>
                A brief history
              </h2>
              <div className="text-[10px] font-mono uppercase tracking-widest"
                   style={{ color: P.saffron }}>
                {heritage.region ? `${heritage.region} India` : "India"}
                {heritage.formed ? ` · Formed ${heritage.formed}` : ""}
                {heritage.capital ? ` · Capital ${heritage.capital}` : ""}
              </div>
            </div>
            <p className="leading-relaxed" style={{ color: P.inkSoft }}>
              {heritage.history}
            </p>
            {heritage.languages?.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <span className="text-[10px] font-mono uppercase tracking-widest"
                      style={{ color: P.saffron }}>Languages</span>
                {heritage.languages.map((l) => (
                  <span key={l}
                        className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs border"
                        style={{ borderColor: P.saffronSoft, color: P.ink, background: "#fff" }}>
                    {l}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Two-column grid — Famous families & Communities */}
          <div className="grid gap-6 md:grid-cols-2">
            {heritage.famousFamilies?.length > 0 && (
              <div className="rounded-2xl border p-6 bg-white"
                   style={{ borderColor: P.saffronSoft }}
                   data-testid="ift-state-families">
                <div className="flex items-center gap-2 mb-3">
                  <BookOpen className="w-4 h-4" style={{ color: P.saffron }} />
                  <h3 className="font-display text-xl">Famous families & dynasties</h3>
                </div>
                <ul className="space-y-3">
                  {heritage.famousFamilies.map((f, i) => (
                    <li key={i} className="border-b last:border-0 pb-3 last:pb-0"
                        style={{ borderColor: P.saffronSoft }}>
                      <div className="font-semibold" style={{ color: P.ink }}>{f.name}</div>
                      <div className="text-sm mt-0.5" style={{ color: P.inkSoft }}>{f.note}</div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {heritage.communities?.length > 0 && (
              <div className="rounded-2xl border p-6 bg-white"
                   style={{ borderColor: P.saffronSoft }}
                   data-testid="ift-state-communities">
                <div className="flex items-center gap-2 mb-3">
                  <Users className="w-4 h-4" style={{ color: P.saffron }} />
                  <h3 className="font-display text-xl">Communities & clans</h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  {heritage.communities.map((c) => (
                    <span key={c}
                          className="inline-flex items-center rounded-full px-3 py-1 text-xs border"
                          style={{ borderColor: P.saffronSoft, color: P.ink, background: "#FFF9F1" }}>
                      {c}
                    </span>
                  ))}
                </div>
                {heritage.gotras?.length > 0 && (
                  <div className="mt-5 pt-4 border-t" style={{ borderColor: P.saffronSoft }}>
                    <div className="flex items-center gap-2 mb-2">
                      <ScrollText className="w-3.5 h-3.5" style={{ color: P.green }} />
                      <span className="text-[10px] font-mono uppercase tracking-widest"
                            style={{ color: P.green }}>
                        Common gotras / clan names
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {heritage.gotras.map((g) => (
                        <span key={g}
                              className="inline-flex rounded px-2 py-0.5 text-[11px] font-mono border"
                              style={{ borderColor: P.greenSoft, color: P.green, background: "#F4FBF7" }}>
                          {g}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Migration corridors */}
          {heritage.corridors?.length > 0 && (
            <div className="rounded-2xl border p-6 bg-white"
                 style={{ borderColor: P.saffronSoft }}
                 data-testid="ift-state-corridors">
              <div className="flex items-center gap-2 mb-3">
                <Compass className="w-4 h-4" style={{ color: P.saffron }} />
                <h3 className="font-display text-xl">Migration corridors from {stateName}</h3>
              </div>
              <ul className="space-y-3">
                {heritage.corridors.map((c, i) => (
                  <li key={i} className="flex flex-wrap items-baseline gap-2 border-b last:border-0 pb-2 last:pb-0"
                      style={{ borderColor: P.saffronSoft }}>
                    <span style={{ color: P.saffron }}>→</span>
                    <span className="font-display italic text-lg" style={{ color: P.ink }}>
                      {c.to}
                    </span>
                    <span className="basis-full text-sm ml-6" style={{ color: P.inkSoft }}>
                      {c.note}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}

      <section className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-4 h-4" style={{ color: P.saffron }} />
          <h2 className="font-display text-2xl">Trees from {stateName}</h2>
        </div>
        {loading && (
          <div className="text-center py-8 opacity-60"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
        )}
        {!loading && trees.length === 0 && (
          <div className="rounded-2xl border p-8 text-center bg-white" style={{ borderColor: P.saffronSoft }} data-testid="ift-state-empty">
            <TreePine className="w-8 h-8 mx-auto opacity-30 mb-2" />
            <div>No trees documented from {stateName} yet.</div>
            <Link to="/family-trees/new" className="inline-flex items-center gap-1 mt-3 font-semibold text-sm" style={{ color: P.saffron }}>
              Start a family tree <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        )}
        {!loading && trees.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="ift-state-trees">
            {trees.map((t) => (
              <Link
                key={t.id}
                to={`/family/${t.slug || t.id}`}
                className="block rounded-2xl border p-4 bg-white hover:shadow-lg transition"
                style={{ borderColor: P.saffronSoft }}
                data-testid={`ift-state-tree-${t.slug || t.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-11 h-11 rounded-full flex items-center justify-center shrink-0" style={{ background: P.saffronSoft, color: P.saffron }}>
                    <TreePine className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{t.name}</div>
                    <div className="text-xs mt-0.5" style={{ color: P.inkSoft }}>
                      {[t.origin_district, t.origin_state, t.country].filter(Boolean).join(", ") || t.origin_location || stateName}
                    </div>
                    <div className="text-[10px] font-mono uppercase mt-2 opacity-70">
                      {t.member_count ? `${t.member_count} members` : "New tree"}
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* JSON-LD for state page indexing */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context":   "https://schema.org",
            "@type":      "CollectionPage",
            "name":       `${stateName} Family Trees`,
            "description":`Family trees, memorials, and heritage records from ${stateName}, India.`,
            "url":        `https://indianfamilytree.com/state/${stateSlug}`,
            "about":      { "@type": "Place", "name": `${stateName}, India` },
            "numberOfItems": data.total_trees,
          }),
        }}
      />
    </div>
  );
}
